import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import ReactSelect, { components } from "react-select";
import { Button, Checkbox, makeStyles, useTheme } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { IPAD_LANDSCAPE_BREAKPOINT } from "../../theme/breakpoints";

const useStyles = makeStyles((theme) => ({
  select: {
    // width: "180px",
    width: "100%",
    height: "48px",
    borderRadius: 25,
    // color: "#707070",
    fontSize: "13px",
    fontWeight: 600,
    display: "inline-block",
    [theme.breakpoints.between(
      IPAD_LANDSCAPE_BREAKPOINT.minWidth,
      IPAD_LANDSCAPE_BREAKPOINT.maxWidth
    )]: {
      height: "35px",
    },
  },
  dropdownIcon: {
    color: theme.palette.borderColor?.default,
    transition: "transform 0.3s ease-in-out",
  },
  dropdownContainer: {
    marginRight: theme.spacing(1),
  },
  open: {
    transform: "rotate(180deg)", // Rotate the icon when menu is open
  },
  buttonsWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  actionButtonsWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 8,
  },
  okButton: {
    display: "relative",
    paddingLeft: "0.5px",
    paddingRight: "0.5px",
    minWidth: "25%",
    width: "25%",
    height: "3%",
    margin: "0.5em",
    fontSize: "0.8em",
    backgroundColor: "#82c1a7",
    color: "#eff7ff",
    "&:hover": {
      backgroundColor: "#82c1a7",
    },
  },
  cancelButton: {
    display: "relative",
    fontSize: "0.8em",
    height: "3.5%",
    marginLeft: "1em",
    paddingLeft: "1px",
    paddingRight: "1px",
    background: "transparent",
    boxShadow: "none",
    color: "#2e75b5",
  },
  actionButton: {
    background: `linear-gradient(#00A19A,#4582E8,#2F7DFF)`,
    color: theme.palette.common.white,
    borderRadius: 5,
  },
}));

const DropdownIndicator = ({ selectProps, ...props }) => {
  const classes = useStyles();
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator
        {...props}
        className={classes.dropdownContainer}
      >
        <ArrowDropDownIcon
          className={`${classes.dropdownIcon} ${
            selectProps.menuIsOpen && classes.open
          }`}
        />
      </components.DropdownIndicator>
    )
  );
};

DropdownIndicator.propTypes = {
  selectProps: PropTypes.object.isRequired, // Validate selectProps object
};

const CheckboxOption = ({
  children,
  isSelected,
  selectProps,
  ...otherProps
}) => (
  <components.Option {...otherProps}>
    <Checkbox
      type="checkbox"
      onChange={() => null}
      checked={isSelected || selectProps.selectAllChecked}
      style={{ color: `#A989EE` }}
      size="small"
    />
    {children}
  </components.Option>
);

CheckboxOption.propTypes = {
  children: PropTypes.node.isRequired,
  isSelected: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
  value: PropTypes.isRequired,
};

const ValueContainer = ({ children, ...props }) => (
  <components.ValueContainer {...props}>
    <div style={{ display: "flex", alignItems: "center" }}>{children}</div>
  </components.ValueContainer>
);

ValueContainer.propTypes = {
  children: PropTypes.node,
};

const Menu = ({ selectProps, ...menuProps }) => {
  const classes = useStyles();

  const {
    setOpen,
    onSubmit,
    isMulti,
    resetFilter,
    actionButtons,
    onChange,
  } = selectProps;
  const handleOkClick = () => {
    const selectedValues = menuProps.getValue();
    onSubmit(selectedValues);
    setOpen(false);
  };

  const handleActionClick = (action) => {
    const selectedValues = menuProps.getValue();
    action?.onClick?.(selectedValues);
    if (action.label === "Cancel") {
      onChange([]);
    }
    setOpen(false);
  };

  return isMulti ? (
    <components.Menu {...menuProps}>
      {menuProps.children}
      <div className={classes.buttonsWrapper}>
        <Button
          onClick={() => {
            resetFilter();
          }}
          onTouchEnd={() => resetFilter()}
          variant="secondary"
          className={classes.cancelButton}
        >
          Clear
        </Button>
        <Button
          onClick={() => handleOkClick()}
          onTouchEnd={() => handleOkClick()}
          className={classes.okButton}
        >
          OK
        </Button>
      </div>
    </components.Menu>
  ) : (
    <components.Menu {...menuProps}>
      {menuProps.children}
      <div className={classes.actionButtonsWrapper} aria-hidden="true">
        {actionButtons?.map((button) => (
          <Button
            key={button.id}
            onClick={() => handleActionClick(button)}
            variant={button.variant || "contained"}
            className={clsx(classes.actionButton, button.className)}
          >
            {button.label}
          </Button>
        ))}
      </div>
    </components.Menu>
  );
};
Menu.propTypes = {
  menuProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};

const Dropdown = ({
  label,
  options,
  selectClassName,
  selectName,
  maxMenuHeight = 660,
  onChange,
  isLoading,
  placeholder,
  value,
  isMulti = false,
  isClearable = false,
  onSubmit,
  actionButtons = [],
  isDisabled= false,
  borderColor: propBorderColor,
  ...props
}) => {
  const classes = useStyles();

  const theme = useTheme();

  const borderColor = propBorderColor || theme.palette.borderColor;

  const [selectedOptions, setSelectedOptions] = useState(value);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const handleOptionChange = useCallback(
    (optionValue) => {
      if (isMulti) {
        if (optionValue.some((opt) => opt.value === "*")) {
          if (selectAllChecked) {
            setSelectedOptions([]);
            setSelectAllChecked(false);
          } else {
            setSelectedOptions(options);
            setSelectAllChecked(true);
          }
        } else {
          setSelectedOptions(optionValue);
          setSelectAllChecked(false);
        }
      } else {
        setSelectedOptions(optionValue);
        onChange(optionValue);
        // setOpen(false)
      }
    },
    [isMulti, onChange, options, selectAllChecked]
  );

  useEffect(() => {
    const allSelected = selectedOptions?.length === options.length;
    setSelectAllChecked(allSelected);
  }, [selectedOptions, options, value]);

  useEffect(() => {
    setSelectedOptions(value);
  }, [options, value]);

  // eslint-disable-next-line no-unused-vars
  const allOption = {
    value: "*",
    label: "All",
    isSelected: selectAllChecked,
  };

  const customComponents = {
    Option: isMulti ? CheckboxOption : components.Option,
    DropdownIndicator,
    ValueContainer,
    Menu,
  };

  const resetFilter = () => {
    setSelectedOptions([]);
    onChange([]);
  };
  return (
    <div {...props}>
      {label}
      <ReactSelect
        ref={menuRef}
        isLoading={isLoading}
        options={options}
        className={clsx(classes.select, selectClassName)}
        classNamePrefix={`select-${selectName}`}
        maxMenuHeight={isMulti ? 310 : maxMenuHeight}
        components={customComponents}
        placeholder={placeholder}
        menuPlacement="auto"
        menuPosition="fixed"
        isSearchable={false}
        isMulti={isMulti}
        closeMenuOnSelect={actionButtons.length <= 0}
        hideSelectedOptions={false}
        isClearable={isClearable}
        menuIsOpen={open}
        onMenuOpen={() => setOpen(true)}
        onMenuClose={() => setOpen(false)}
        blurInputOnSelect={false}
        isDisabled={isDisabled}
        styles={{
          menu: () => ({
            width: "100%",
            backgroundColor: theme.palette.common.white,
            borderRadius: theme.spacing(1),
            boxShadow: "0px 3px 6px #00000029",
            zIndex: 9999,
          }),
          menuList: (provided) => ({
            ...provided,
            "&::-webkit-scrollbar": {
              width: "4px !important",
              height: "6px",
              display: "block !important",
              background: "none",
            },
            "&::-webkit-scrollbar-track": {
              background: "none",
            },
            "&::-webkit-scrollbar-thumb": {
              background: `${borderColor?.default}`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: `${borderColor?.default}`,
            },
            textTransform:'capitalize'
          }),
          control: (provided) => ({
            ...provided,
            minHeight: 0,
            height: "100%",
            padding: 0,
            borderRadius: 10,
            border: `1px solid ${borderColor?.default}`,
            boxShadow: "none",
            "&:hover": {
              border: `1px solid ${borderColor?.default}`,
              boxShadow: "none",
            },
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            width: "30px",
            padding: "5px",
          }),
          option: (provided, state) => ({
            ...provided,
            fontWeight: theme.typography.fontWeightRegular,
            color: state.isSelected
              ? theme.palette.common.white
              : theme.palette.grey[800],
            backgroundColor: state.isSelected
              ? `${borderColor?.default}`
              : theme.palette.common.white,
            "&:hover": {
              backgroundColor: "#A989EE1F",
            },
            cursor: "pointer",
            ...(isMulti ? { padding: 0, paddingLeft: 8, paddingRight: 8 } : {}),
          }),
          singleValue: (provided, state) => ({
            ...provided,
            fontWeight: theme.typography.fontWeightRegular,
            color: state.isSelected
              ? theme.palette.common.white
              : theme.palette.grey[800],
            textTransform:'capitalize'
          }),
          multiValue: (provided, state) => ({
            ...provided,
            fontWeight: theme.typography.fontWeightRegular,
            color: state.isSelected
              ? theme.palette.common.white
              : theme.palette.grey[800],
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            display: "none",
          }),
          placeholder: (provided) => ({
            ...provided,
            fontWeight: theme.typography.fontWeightRegular,
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          multiValueRemove: (provided) => ({
            ...provided,
            display: "none", // Hide the clear icon for selected options
          }),
        }}
        onChange={handleOptionChange}
        value={selectedOptions}
        onSubmit={onChange}
        setOpen={setOpen}
        selectAllChecked={selectAllChecked}
        resetFilter={resetFilter}
        actionButtons={actionButtons}
        {...props}
      />
    </div>
  );
};

Dropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  selectClassName: PropTypes.string,
  selectName: PropTypes.string.isRequired,
  maxMenuHeight: PropTypes.number,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.object, // For single-select, value is an object
    PropTypes.arrayOf(PropTypes.object), // For multi-select, value is an array of objects
  ]),
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  actionButtons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      variant: PropTypes.oneOf(["text", "outlined", "contained"]),
      className: PropTypes.string,
    })
  ),
  isDisabled:PropTypes.bool,
  onSubmit: PropTypes.func,
  borderColor: PropTypes.any,
};

export default Dropdown;
